import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, MessageSquare, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import AdSlot from "../components/AdSlot";
import { API } from "../lib/api";

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    user_name: "",
    user_email: "",
    rating: 5,
    title: "",
    content: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews/approved`);
      setReviews(res.data.reviews);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      toast.success("Thank you! Your review will be published after approval.");
      setShowForm(false);
      setFormData({ user_name: "", user_email: "", rating: 5, title: "", content: "" });
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star 
          key={star} 
          className={`w-4 h-4 ${star <= rating ? 'fill-cyan-400 text-cyan-400' : 'text-slate-300'}`} 
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0EEEB]">
      <SiteHeader />
      
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-literary text-3xl sm:text-4xl text-foreground" data-testid="reviews-title">
              What Writers Are Saying
            </h1>
            <p className="text-muted-foreground mt-2">Real stories from our Shweebum family</p>
          </motion.div>

          <div className="text-center mb-8">
            <Button 
              onClick={() => setShowForm(!showForm)}
              className="bg-cyan-700 hover:bg-cyan-800"
              data-testid="write-review-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {showForm ? "Cancel" : "Write a Review"}
            </Button>
          </div>

          {/* Review Form */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
            >
              <Card className="bg-card mb-8">
                <CardContent className="p-6">
                  <h3 className="font-literary text-xl mb-4">Share Your Experience</h3>
                  <form onSubmit={handleSubmitReview} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Your Name *</Label>
                        <Input
                          value={formData.user_name}
                          onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                          required
                          data-testid="review-name"
                        />
                      </div>
                      <div>
                        <Label>Your Email *</Label>
                        <Input
                          type="email"
                          value={formData.user_email}
                          onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                          required
                          data-testid="review-email"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Rating</Label>
                      <div className="flex gap-2 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setFormData({ ...formData, rating: star })}
                            className="focus:outline-none"
                          >
                            <Star 
                              className={`w-8 h-8 transition-colors ${star <= formData.rating ? 'fill-cyan-400 text-cyan-400' : 'text-slate-300 hover:text-cyan-300'}`} 
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Review Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Summarize your experience"
                        required
                        data-testid="review-title"
                      />
                    </div>
                    <div>
                      <Label>Your Review *</Label>
                      <Textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        placeholder="Tell us about your experience with Shweebum..."
                        className="min-h-[120px]"
                        required
                        data-testid="review-content"
                      />
                    </div>
                    <Button type="submit" disabled={submitting} className="bg-cyan-700 hover:bg-cyan-800">
                      {submitting ? "Submitting..." : "Submit Review"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Reviews List */}
          <AdSlot slot="reviews-top" format="inline" className="max-w-4xl mx-auto" />
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-card h-32 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <Quote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-muted-foreground">No reviews yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="bg-card">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-foreground">{review.title}</h3>
                          <p className="text-sm text-muted-foreground">{review.user_name}</p>
                        </div>
                        <StarRating rating={review.rating} />
                      </div>
                      <p className="text-foreground mt-3">{review.content}</p>
                      <p className="text-xs text-muted-foreground mt-4">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ReviewsPage;
